/**
 * TrialBalanceSection — dashboard panel for the Trial Balance.
 *
 * PERIOD FILTER: the same Company + Period SectionFilter every other section
 * uses. This panel was Company-only on the belief that a trial balance is a
 * latest-snapshot per ledger with no range to filter by. That was never true of
 * the backend: GET /trial-balance already computes ANY window live when from/to
 * are supplied, and computes it correctly — the SQL folds every posting before
 * `from` into the opening balance, so closing = opening + movements holds for
 * an arbitrary range, not just the two windows that happen to be stored.
 *
 * THE PERIOD MUST FOLLOW THE DRILL-DOWN. Every level below the summary —
 * groups under a primary group, ledgers under a group — takes the same from/to,
 * otherwise clicking into a April-only figure would open a drill-down showing
 * the whole year and the two views would disagree with no indication why.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionFilter from './SectionFilter';
import TrialBalanceTable from './TrialBalanceTable';
import DrillDownModal from './DrillDownModal';
import { useTrialBalanceByCompany } from '../hooks/useDashboard';
import useDrillDown from '../hooks/useDrillDown';
import dashboardAPI from '../services/dashboardAPI';
import { computeRangeForFilter } from '../utils/dateFilters';

const PAGE_SIZE = 25;

const fmtCount = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

const fmtPeriod = (from, to) => {
  if (!from && !to) return '';
  const d = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  if (from && to) return `${d(from)} – ${d(to)}`;
  return from ? `from ${d(from)}` : `up to ${d(to)}`;
};

const TrialBalanceSection = ({ companies }) => {
  const [companyId, setCompanyId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [level, setLevel] = useState("primary");

  // 'overall' keeps the previous behaviour exactly: no from/to is sent, and the
  // backend serves the stored snapshot rather than computing a window.
  const [dateFilter, setDateFilter] = useState('overall');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const { from, to } = useMemo(() => {
    if (dateFilter === 'custom') return { from: customFrom, to: customTo };
    return computeRangeForFilter(dateFilter);
  }, [dateFilter, customFrom, customTo]);

const [displayRows, setDisplayRows] = useState([]);

const [primaryGroup, setPrimaryGroup] = useState(null);

const [groupName, setGroupName] = useState(null);

const [breadcrumb, setBreadcrumb] = useState(["Trial Balance"]);
const [groupStack, setGroupStack] = useState([]);

  const { data: rows, loading, error } = useTrialBalanceByCompany(companyId, companies, from, to);
  const isAllCompanies = companyId === 'all';

  const drill = useDrillDown();
  useEffect(() => {
    setDisplayRows(rows || []);
    setLevel("primary");
    setPrimaryGroup(null);
    setGroupName(null);
    setBreadcrumb(["Trial Balance"]);
}, [rows]);

  // A trial balance row already carries companyId, ledgerGuid and the period
  // the snapshot was computed for — so the ledger opens for exactly the window
  // the row's figures describe, and the two agree.
  const handleOpenLedger = useCallback((row) => {
    drill.openLedger({
      companyId: row.companyId,
      ledgerGuid: row.ledgerGuid,
      ledgerName: row.ledgerName,
      // A stored snapshot row carries the period it was computed for. A row
      // from a live-computed window does not, so fall back to the window the
      // user actually selected — otherwise the ledger would open unbounded
      // while the figure that was clicked covers only the chosen dates.
      from: row.fromDate ? String(row.fromDate).slice(0, 10) : (from || undefined),
      to: row.toDate ? String(row.toDate).slice(0, 10) : (to || undefined),
    });
  }, [drill, from, to]);

 const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) return displayRows || [];


    return (displayRows || []).filter(r =>
        String(
            r.groupName ||
            r.ledgerName
        ).toLowerCase().includes(q)
    );
}, [displayRows, searchTerm]);
const openingTotals = useMemo(
  () =>
    filteredRows.reduce(
      (acc, r) => {
        acc.debit += Number(r.openingDebit) || 0;
        acc.credit += Number(r.openingCredit) || 0;
        return acc;
      },
      { debit: 0, credit: 0 }
    ),
  [filteredRows]
);
const loadGroups = async (primary) => {

    const json = await dashboardAPI.getTrialBalanceGroups(companyForDrill(null), primary, from, to);

    setDisplayRows(json.data);
console.log("loadGroups",json.data);
    setPrimaryGroup(primary);

    setLevel("group");

    setBreadcrumb([
        "Trial Balance",
        primary
    ]);
};
// Drill-down needs ONE company. The selected filter can be "all", which is a
// perfectly good scope for the summary but meaningless one level down — two
// companies can have a group of the same name and merging them would be a
// fiction. Every trial balance row carries its own companyId, so use that and
// the drill-down works from the All Companies view as well.
//
// Sending "all" through used to reach Postgres verbatim and 500 the request
// (pg_strtoint32 on 'all'); the backend now rejects it with a clear 400, and
// this stops it being sent in the first place.
const [drillCompanyId, setDrillCompanyId] = useState(null);
const companyForDrill = (row) =>
  (companyId !== 'all' ? companyId : (row?.companyId ?? drillCompanyId));

const loadLedgers = async (group, forCompanyId) => {
setGroupStack(prev => [...prev, group]);
    const json = await dashboardAPI.getTrialBalanceByGroup(forCompanyId, group, from, to);
console.log("loadLedgers",json.data);
    setDisplayRows(json.data);

    setGroupName(group);

    setLevel("ledger");

    setBreadcrumb([
        "Trial Balance",
        primaryGroup,
        group
    ]);
};
const handleRowClick = async (row) => {

  if (row.ledgerGuid === "__OPENING_STOCK__") {
    drill.openInventory({
        companyId: row.companyId
    });
    return;
}
    // Ledger -> open ledger statement
    if (row.type === "ledger") {
        handleOpenLedger(row);
        return;
    }

    // Group -> drill further, scoped to the company the row belongs to.
    const target = companyForDrill(row);
    if (!target) return;              // nothing sensible to drill into
    setDrillCompanyId(target);
    await loadLedgers(row.groupName, target);
};

const goBack = async () => {

    // Already at primary
    if (groupStack.length === 0) {
        return;
    }

    // Going back to primary
    if (groupStack.length === 1) {
console.log("Primary rows:", rows);
        setDisplayRows(rows);
        setGroupStack([]);
        setBreadcrumb(["Trial Balance"]);
        setLevel("primary");
        return;
    }

    // Go back one group
    const newStack = groupStack.slice(0, -1);

    setGroupStack(newStack);

    const previousGroup = newStack[newStack.length - 1];

    const json = await dashboardAPI.getTrialBalanceByGroup(
      companyForDrill(null), previousGroup, from, to);

    setDisplayRows(json.data);

    setBreadcrumb([
        "Trial Balance",
        ...newStack
    ]);
};
  const totals = useMemo(() => filteredRows.reduce(
    (acc, r) => {
      acc.debit += Number(r.closingDebit) || 0;
      acc.credit += Number(r.closingCredit) || 0;
      return acc;
    },
    { debit: 0, credit: 0 }
  ), [filteredRows]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [companyId, searchTerm, from, to]);

  const handleReset = () => {
    setCompanyId('all');
    setSearchTerm('');
    setPage(1);
    setDateFilter('overall');
    setCustomFrom('');
    setCustomTo('');
    setDrillCompanyId(null);
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Trial Balance</h2>
          <p className="panel-subtitle">
            {fmtCount(filteredRows.length)} ledger(s){isAllCompanies ? ' across all companies' : ''}
            {/* Always name the window on screen. Without it a filtered figure is
                indistinguishable from an unfiltered one, and "the numbers look
                wrong" is impossible to diagnose from a screenshot. */}
            {fmtPeriod(from, to) ? ` · ${fmtPeriod(from, to)}` : ' · all dates'}.
          </p>
        </div>
        <input
          type="search"
          className="txn-search"
          placeholder="Search ledger name…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <SectionFilter
        idPrefix="trial-balance"
        companies={companies}
        companyId={companyId}
        onCompanyChange={setCompanyId}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        customFrom={customFrom}
        onCustomFromChange={setCustomFrom}
        customTo={customTo}
        onCustomToChange={setCustomTo}
        onReset={handleReset}
      />
<div className="tb-breadcrumb">

    {breadcrumb.map((b,i)=>

        <span key={i}>

            {i>0 && " > "}

            {b}

        </span>

    )}

</div>
{level !== "primary" && (
  <button
    type="button"
    className="pagination-button"
    onClick={goBack}
    style={{ marginBottom: "10px" }}
  >
    ← Back
  </button>
)}
      <TrialBalanceTable
        rows={pagedRows}
        totals={totals}
        openingTotals={openingTotals}
        loading={loading}
        error={error}
        showCompany={isAllCompanies}
        onOpenLedger={handleRowClick}
        level={level}
      />

      {filteredRows.length > 0 && (
        <div className="pagination-controls">
          <button type="button" className="pagination-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
            ← Previous
          </button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" className="pagination-button" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>
            Next →
          </button>
        </div>
      )}

      {drill.isOpen && (
        <DrillDownModal
          stack={drill.stack}
          onPush={drill.push}
          onPopTo={drill.popTo}
          onClose={drill.close}
        />
      )}
    </section>
  );
};

export default TrialBalanceSection;
